import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";

import { setInitialTransactionPin } from "../../services/UserService";
import { updateAuthUser } from "../../store/actions/Auth/AuthActions";
import { readErrorEnvelope } from "../../utils/errorEnvelope";
import useGuidedTour from "../../hooks/useGuidedTour";
import PinInput from "../components/PinInput";
import {
  SET_TRANSACTION_PIN_TOUR_ID,
  buildSetTransactionPinTourSteps,
} from "./SetTransactionPin.tour";

const SetTransactionPin = () => {
  const { authUser } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Only fire the guided tour when the user actually arrived here from the
  // security walkthrough handoff. PermissionGate also lands wallet holders
  // without a PIN on this page on every login, and the tour is not the
  // right thing to fire on that path.
  const arrivedFromSecurityTour = searchParams.get('fromSecurityTour') === '1';
  useGuidedTour(
    SET_TRANSACTION_PIN_TOUR_ID,
    buildSetTransactionPinTourSteps(),
    { enabled: !!authUser && authUser.has_transaction_pin === false && arrivedFromSecurityTour },
  );

  const redirectTarget = searchParams.get("redirect") || "/";

  const [formData, setFormData] = useState({
    newPin: "",
    newPinConfirmation: "",
  });

  const setPinMutation = useMutation({
    mutationFn: setInitialTransactionPin,
    onSuccess: (response) => {
      toast.success(response.data?.message || "Transaction PIN set successfully.");
      // Patch the local auth-user snapshot so the PermissionGate stops bouncing
      // the destination back to this page. The next authenticated fetch replaces
      // this with the authoritative expiry date; null hides the expiry banner
      // in the interim.
      dispatch(updateAuthUser({ has_transaction_pin: true, transaction_pin_expiry_date: null }));
      navigate(redirectTarget, { replace: true });
    },
    onError: (err) => {
      const { message } = readErrorEnvelope(err);
      toast.error(message, { autoClose: 20000, draggable: true, closeButton: true });
    },
  });

  if (authUser?.has_transaction_pin) {
    return <Navigate to={redirectTarget} replace />;
  }

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPinMutation.mutate({
      partnerId: authUser.partner_id,
      userId: authUser.id,
      ...formData,
    });
  };

  const submitDisabled =
    !formData.newPin ||
    !formData.newPinConfirmation ||
    setPinMutation.isPending;

  return (
    <div className="row justify-content-center">
      <div className="col-12 col-md-8 col-lg-6">
        <div className="card" data-tour="set-transaction-pin-card">
          <div className="card-header">
            <h4 className="card-title mb-0">Set your Transaction PIN</h4>
          </div>
          <div className="card-body">
            <p className="text-muted">
              Every payout requires a six-digit PIN. Pick one you can remember, avoiding obvious sequences
              (like <code>123456</code>), repeated digits (like <code>111111</code>), or anything derived from your
              phone number. Once set, you will use it to authorise every transaction.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="form-group mb-3">
                <label className="form-label text-primary" htmlFor="newPin">
                  New Transaction PIN
                </label>
                <PinInput
                  placeholder="******"
                  id="newPin"
                  data-tour="set-transaction-pin-new-pin"
                  value={formData.newPin}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group mb-3">
                <label className="form-label text-primary" htmlFor="newPinConfirmation">
                  Confirm Transaction PIN
                </label>
                <PinInput
                  placeholder="******"
                  id="newPinConfirmation"
                  value={formData.newPinConfirmation}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={submitDisabled}
              >
                {setPinMutation.isPending && <Spinner animation="border" size="sm" className="me-2" />}
                Save PIN
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetTransactionPin;
